import { formatDateLabel, toArray, toNullableNumber, toNumberValue, toStringValue, toTimestamp } from '@/features/charting-next/adapters/chartingAdapterUtils';

const MISSING_VALUE_LABEL = 'N/D';
const CHART_COLORS = ['#0ea5e9', '#14b8a6', '#f59e0b', '#6366f1', '#ef4444', '#8b5cf6'];

const ACTIVITY_LABELS = {
    adult: 'Adultos',
    alcoholic: 'Alcohol',
    animalTesting: 'Experimentacion con animales',
    catholic: 'Catolico',
    controversialWeapons: 'Armas de gran calibre',
    smallArms: 'Armas de pequeno calibre',
    furLeather: 'Piel de cuero',
    gambling: 'Juegos de azar',
    gmo: 'Transgenico',
    militaryContract: 'Contrato militar',
    nuclear: 'Nuclear',
    pesticides: 'Pesticidas',
    palmOil: 'Aceite de palma',
    coal: 'Carbon',
    tobacco: 'Tabaco'
};

const formatNumber = (value, options = {}) => {
    const normalized = toNullableNumber(value);

    if (normalized === null) {
        return MISSING_VALUE_LABEL;
    }

    const { compact = false, maximumFractionDigits = 2, minimumFractionDigits = 0, suffix = '', unit = '' } = options;
    const formatted = new Intl.NumberFormat('es-ES', {
        notation: compact ? 'compact' : 'standard',
        compactDisplay: 'short',
        maximumFractionDigits,
        minimumFractionDigits
    }).format(normalized);

    return `${formatted}${suffix}${unit ? ` ${unit}` : ''}`;
};

const formatFullNumber = (value, options = {}) => {
    const normalized = toNullableNumber(value);

    if (normalized === null) {
        return '';
    }

    const { maximumFractionDigits = 2, minimumFractionDigits = 0, suffix = '', unit = '' } = options;
    const formatted = new Intl.NumberFormat('es-ES', {
        maximumFractionDigits,
        minimumFractionDigits
    }).format(normalized);

    return `${formatted}${suffix}${unit ? ` ${unit}` : ''}`;
};

const hasNumericValue = (value) => toNullableNumber(value) !== null;

const compactShares = (value) =>
    formatNumber(value, {
        compact: true,
        maximumFractionDigits: 2,
        unit: 'acciones'
    });

const compactEmployees = (value) =>
    formatNumber(value, {
        compact: true,
        maximumFractionDigits: 2,
        unit: 'empleados'
    });

const formatPercentValue = (value) =>
    formatNumber(value, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        suffix: ' %'
    });

const formatRatioValue = (value) =>
    formatNumber(value, {
        maximumFractionDigits: 2,
        suffix: 'x'
    });

const formatCurrencyLikeValue = (value, currencyLabel = '') =>
    formatNumber(value, {
        compact: Math.abs(Number(value)) >= 1000000,
        maximumFractionDigits: 2,
        unit: currencyLabel
    });

const createMetric = ({ id, label, value, formatter = formatNumber, fullFormatter = formatFullNumber }) => ({
    id,
    label,
    value: toNullableNumber(value),
    displayValue: formatter(value),
    fullValue: fullFormatter(value),
    isAvailable: hasNumericValue(value)
});

const createMetricSection = (id, title, metrics) => ({
    id,
    title,
    metrics,
    hasData: metrics.some((metric) => metric.isAvailable)
});

const readYearlyEntries = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (value && typeof value === 'object') {
        return Object.values(value);
    }

    return [];
};

const resolveSeriesLabel = (value, fallbackIndex) => {
    const rawValue = toStringValue(value);

    if (!rawValue) {
        return `Dato ${fallbackIndex + 1}`;
    }

    if (/^\d{4}/.test(rawValue)) {
        return rawValue.slice(0, 4);
    }

    const parsedDate = new Date(rawValue);

    if (!Number.isNaN(parsedDate.getTime())) {
        return new Intl.DateTimeFormat('es-ES', {
            year: 'numeric'
        }).format(parsedDate);
    }

    return rawValue;
};

const resolveSeriesSortValue = (value, fallbackIndex) => {
    const rawValue = toStringValue(value);
    const parsedDate = Date.parse(rawValue);

    if (Number.isFinite(parsedDate)) {
        return parsedDate;
    }

    const parsedNumber = Number(rawValue);

    if (Number.isFinite(parsedNumber)) {
        return parsedNumber;
    }

    return fallbackIndex;
};

const buildChartData = ({ labels, values, title, colorIndex = 0 }) => {
    const borderColor = CHART_COLORS[colorIndex % CHART_COLORS.length];

    return {
        labels,
        datasets: [
            {
                label: title,
                data: values,
                borderColor,
                backgroundColor: `${borderColor}24`,
                borderWidth: 2,
                fill: true,
                tension: 0.32,
                pointRadius: 0,
                pointHoverRadius: 3
            }
        ]
    };
};

const normalizeChartSeries = ({ id, title, entries, valueKey, dateKey = 'date', unitLabel = '', valueFormatter = formatCurrencyLikeValue, colorIndex = 0 }) => {
    const points = readYearlyEntries(entries)
        .map((item, index) => {
            const value = toNullableNumber(item?.[valueKey]);
            const rawDate = item?.[dateKey] ?? item?.dateFormatted ?? item?.date;

            if (value === null) {
                return null;
            }

            return {
                label: resolveSeriesLabel(rawDate, index),
                sortValue: resolveSeriesSortValue(rawDate, index),
                value
            };
        })
        .filter(Boolean)
        .sort((left, right) => left.sortValue - right.sortValue);

    const labels = points.map((point) => point.label);
    const values = points.map((point) => point.value);
    const latestPoint = points[points.length - 1] || null;

    return {
        id,
        title,
        unitLabel,
        labels,
        values,
        latestLabel: latestPoint ? valueFormatter(latestPoint.value, unitLabel) : MISSING_VALUE_LABEL,
        hasData: points.length > 0,
        chartData: buildChartData({
            labels,
            values,
            title,
            colorIndex
        })
    };
};

const normalizeFundamentalRows = (items, normalizer) => toArray(items).map(normalizer).filter(Boolean);

const normalizeInstitutionHolder = (item, index) => {
    const name = toStringValue(item?.name || item?.Name);

    if (!name) {
        return null;
    }

    const currentShares = toNullableNumber(item?.currentShares);

    return {
        id: `${name}-${index}`,
        institution: name,
        currentShares,
        currentSharesLabel: compactShares(currentShares),
        currentSharesFullLabel: formatFullNumber(currentShares, {
            maximumFractionDigits: 0,
            unit: 'acciones'
        })
    };
};

const normalizeOfficer = (item, index) => {
    const name = toStringValue(item?.Name || item?.name);

    if (!name) {
        return null;
    }

    return {
        id: `${name}-${index}`,
        name,
        title: toStringValue(item?.Title || item?.title, MISSING_VALUE_LABEL)
    };
};

const normalizeActivity = (item, index) => {
    const activity = toStringValue(item?.Activity || item?.activity);

    if (!activity) {
        return null;
    }

    return {
        id: `${activity}-${index}`,
        activity,
        label: ACTIVITY_LABELS[activity] || activity,
        involvement: toStringValue(item?.Involvement || item?.involvement, MISSING_VALUE_LABEL)
    };
};

export function normalizeChartingStreamingState(payload = {}) {
    return {
        isLive: toStringValue(payload?.status) === 'live',
        streamingDate: toStringValue(payload?.streaming_date),
        streamingUrl: toStringValue(payload?.streaming_url),
        raw: payload
    };
}

export function normalizeChartingOhlcSeries(payload = {}) {
    const times = toArray(payload?.t);
    const opens = toArray(payload?.o);
    const highs = toArray(payload?.h);
    const lows = toArray(payload?.l);
    const closes = toArray(payload?.c);
    const volumes = toArray(payload?.v);
    const candles = [];
    const volumeBars = [];

    for (let index = 0; index < times.length; index += 1) {
        const time = toNumberValue(times[index], 0);
        const open = toNumberValue(opens[index], 0);
        const high = toNumberValue(highs[index], 0);
        const low = toNumberValue(lows[index], 0);
        const close = toNumberValue(closes[index], 0);
        const volume = toNumberValue(volumes[index], 0);

        if (!time) {
            continue;
        }

        candles.push({
            time,
            open,
            high,
            low,
            close
        });
        volumeBars.push({
            time,
            value: volume,
            color: close >= open ? 'rgba(14, 165, 233, 0.35)' : 'rgba(239, 68, 68, 0.35)'
        });
    }

    return {
        status: toStringValue(payload?.s, candles.length ? 'ok' : 'no_data'),
        candles,
        volumes: volumeBars,
        corporateActions: [],
        firstTimestamp: candles[0]?.time || null,
        lastTimestamp: candles[candles.length - 1]?.time || null,
        raw: payload
    };
}

export function normalizeChartingCorporateActions(payload = []) {
    return toArray(payload)
        .map((item, index) => {
            const time = toNumberValue(item?.time, 0);
            const label = toStringValue(item?.label).toUpperCase();

            if (!time || !label) {
                return null;
            }

            const color = toStringValue(item?.color, label === 'S' ? '#ff0000' : '#0088cc');
            const id = toStringValue(item?.id, `corporate-action-${index}`);
            const tooltip = toArray(item?.tooltip)
                .map((value) => toStringValue(value))
                .filter(Boolean);

            return {
                id,
                time,
                label,
                color,
                tooltip,
                marker: {
                    id,
                    time,
                    color,
                    position: 'belowBar',
                    shape: 'circle',
                    text: label
                },
                raw: item
            };
        })
        .filter(Boolean)
        .sort((left, right) => left.time - right.time);
}

export function normalizeChartingOhlcSeriesWithCorporateActions(payload = {}, corporateActions = []) {
    const series = normalizeChartingOhlcSeries(payload);

    return {
        ...series,
        corporateActions: Array.isArray(corporateActions) ? corporateActions : []
    };
}

export function normalizeChartingFundamental(payload = {}) {
    const sharesStats = payload?.SharesStats || {};
    const highlights = payload?.Highlights || {};
    const valuation = payload?.Valuation || {};
    const general = payload?.General || {};
    const financials = payload?.Financials || {};
    const balanceSheet = financials?.Balance_Sheet || {};
    const cashFlow = financials?.Cash_Flow || {};
    const incomeStatement = financials?.Income_Statement || {};
    const earnings = payload?.Earnings || {};
    const outstandingShares = payload?.outstandingShares || payload?.OutstandingShares || {};
    const holders = payload?.Holders || {};
    const esgScores = payload?.ESGScores || {};
    const currencyCode = toStringValue(general?.CurrencyCode || general?.Currency || highlights?.CurrencyCode);
    const currencySymbol = toStringValue(general?.CurrencySymbol || balanceSheet?.currency_symbol || cashFlow?.currency_symbol || incomeStatement?.currency_symbol);
    const currencyLabel = currencyCode || currencySymbol;
    const shareMetrics = createMetricSection('shares', 'Estado de las acciones', [
        createMetric({
            id: 'sharesOutstanding',
            label: 'Acciones en circulacion',
            value: sharesStats?.SharesOutstanding,
            formatter: compactShares,
            fullFormatter: (value) =>
                formatFullNumber(value, {
                    maximumFractionDigits: 0,
                    unit: 'acciones'
                })
        }),
        createMetric({
            id: 'sharesFloat',
            label: 'Acciones activas',
            value: sharesStats?.SharesFloat,
            formatter: compactShares,
            fullFormatter: (value) =>
                formatFullNumber(value, {
                    maximumFractionDigits: 0,
                    unit: 'acciones'
                })
        }),
        createMetric({
            id: 'percentInsiders',
            label: '% acciones propias',
            value: sharesStats?.PercentInsiders,
            formatter: formatPercentValue,
            fullFormatter: formatPercentValue
        }),
        createMetric({
            id: 'percentInstitutions',
            label: '% acciones en instituciones',
            value: sharesStats?.PercentInstitutions,
            formatter: formatPercentValue,
            fullFormatter: formatPercentValue
        })
    ]);
    const highlightsMetrics = createMetricSection('highlights', 'Datos destacados', [
        createMetric({
            id: 'bookValue',
            label: 'Valor contable',
            value: highlights?.BookValue,
            formatter: (value) => formatCurrencyLikeValue(value, currencyLabel),
            fullFormatter: (value) =>
                formatFullNumber(value, {
                    maximumFractionDigits: 2,
                    unit: currencyLabel
                })
        }),
        createMetric({
            id: 'pegRatio',
            label: 'PEG ratio',
            value: highlights?.PEGRatio,
            formatter: formatRatioValue,
            fullFormatter: formatRatioValue
        }),
        createMetric({
            id: 'peRatio',
            label: 'PER',
            value: highlights?.PERatio,
            formatter: formatRatioValue,
            fullFormatter: formatRatioValue
        })
    ]);
    const valuationMetrics = createMetricSection('valuation', 'Valoracion', [
        createMetric({
            id: 'enterpriseValue',
            label: 'Valor de empresa',
            value: valuation?.EnterpriseValue,
            formatter: (value) => formatCurrencyLikeValue(value, currencyLabel),
            fullFormatter: (value) =>
                formatFullNumber(value, {
                    maximumFractionDigits: 0,
                    unit: currencyLabel
                })
        }),
        createMetric({
            id: 'enterpriseValueEbitda',
            label: 'EV / EBITDA',
            value: valuation?.EnterpriseValueEbitda,
            formatter: formatRatioValue,
            fullFormatter: formatRatioValue
        }),
        createMetric({
            id: 'enterpriseValueRevenue',
            label: 'EV / Ingresos',
            value: valuation?.EnterpriseValueRevenue,
            formatter: formatRatioValue,
            fullFormatter: formatRatioValue
        })
    ]);
    const esgMetrics = createMetricSection('esg', 'Puntuacion ESG', [
        createMetric({
            id: 'totalEsg',
            label: 'Total ESG',
            value: esgScores?.TotalEsg ?? esgScores?.TotalESG
        }),
        createMetric({
            id: 'environmentScore',
            label: 'Ambiental',
            value: esgScores?.EnvironmentScore
        }),
        createMetric({
            id: 'socialScore',
            label: 'Social',
            value: esgScores?.SocialScore
        }),
        createMetric({
            id: 'governanceScore',
            label: 'Gobierno corporativo',
            value: esgScores?.GovernanceScore
        }),
        createMetric({
            id: 'controversyLevel',
            label: 'Nivel de controversia',
            value: esgScores?.ControversyLevel
        })
    ]);
    const financialCharts = [
        normalizeChartSeries({
            id: 'balance',
            title: 'Balance',
            entries: balanceSheet?.yearly,
            valueKey: 'cash',
            unitLabel: currencyLabel || toStringValue(balanceSheet?.currency_symbol),
            colorIndex: 0
        }),
        normalizeChartSeries({
            id: 'cashFlow',
            title: 'Flujo de caja',
            entries: cashFlow?.yearly,
            valueKey: 'endPeriodCashFlow',
            unitLabel: currencyLabel || toStringValue(cashFlow?.currency_symbol),
            colorIndex: 1
        }),
        normalizeChartSeries({
            id: 'incomeStatement',
            title: 'Resultados de ingresos',
            entries: incomeStatement?.yearly,
            valueKey: 'totalRevenue',
            unitLabel: currencyLabel || toStringValue(incomeStatement?.currency_symbol),
            colorIndex: 2
        })
    ].filter((chart) => chart.hasData);
    const earningsChart = normalizeChartSeries({
        id: 'earnings',
        title: 'Ganancias por accion',
        entries: earnings?.Annual,
        valueKey: 'epsActual',
        unitLabel: currencyLabel,
        valueFormatter: (value, unitLabel) =>
            formatNumber(value, {
                maximumFractionDigits: 2,
                unit: unitLabel
            }),
        colorIndex: 3
    });
    const outstandingSharesChart = normalizeChartSeries({
        id: 'outstandingShares',
        title: 'Acciones en mercado',
        entries: outstandingShares?.annual,
        valueKey: 'shares',
        dateKey: 'dateFormatted',
        unitLabel: 'acciones',
        valueFormatter: compactShares,
        colorIndex: 4
    });
    const chartSections = [
        {
            id: 'financials',
            title: 'Finanzas',
            charts: financialCharts
        },
        {
            id: 'earnings',
            title: 'Ganancias por accion',
            charts: earningsChart.hasData ? [earningsChart] : []
        },
        {
            id: 'outstandingShares',
            title: 'Acciones en mercado',
            charts: outstandingSharesChart.hasData ? [outstandingSharesChart] : []
        }
    ].filter((section) => section.charts.length);
    const metricSections = [shareMetrics, highlightsMetrics, valuationMetrics, esgMetrics].filter((section) => section.hasData);
    const institutionHolders = normalizeFundamentalRows(holders?.Institutions, normalizeInstitutionHolder);
    const officers = normalizeFundamentalRows(general?.Officers, normalizeOfficer);
    const activities = normalizeFundamentalRows(esgScores?.ActivitiesInvolvement, normalizeActivity);
    const hasData = Boolean(metricSections.length || chartSections.length || institutionHolders.length || officers.length || activities.length);

    return {
        companyName: toStringValue(general?.Name || payload?.name),
        companyDescription: toStringValue(general?.Description || payload?.description),
        sector: toStringValue(general?.Sector || payload?.sector),
        industry: toStringValue(general?.Industry || payload?.industry),
        country: toStringValue(general?.CountryName || general?.Country || payload?.country),
        exchangeName: toStringValue(general?.Exchange || general?.ExchangeCode || payload?.exchange),
        websiteUrl: toStringValue(general?.WebURL || payload?.websiteUrl || payload?.website),
        currencyCode,
        currencySymbol,
        currencyLabel,
        sharesOutstanding: toNullableNumber(sharesStats?.SharesOutstanding),
        sharesOutstandingLabel: compactShares(sharesStats?.SharesOutstanding),
        sharesFloat: toNullableNumber(sharesStats?.SharesFloat),
        sharesFloatLabel: compactShares(sharesStats?.SharesFloat),
        percentInsiders: toNullableNumber(sharesStats?.PercentInsiders),
        percentInsidersLabel: formatPercentValue(sharesStats?.PercentInsiders),
        percentInstitutions: toNullableNumber(sharesStats?.PercentInstitutions),
        percentInstitutionsLabel: formatPercentValue(sharesStats?.PercentInstitutions),
        bookValue: toNullableNumber(highlights?.BookValue),
        bookValueLabel: formatCurrencyLikeValue(highlights?.BookValue, currencyLabel),
        pegRatio: toNullableNumber(highlights?.PEGRatio),
        pegRatioLabel: formatRatioValue(highlights?.PEGRatio),
        peRatio: toNullableNumber(highlights?.PERatio),
        peRatioLabel: formatRatioValue(highlights?.PERatio),
        enterpriseValue: toNullableNumber(valuation?.EnterpriseValue),
        enterpriseValueLabel: formatCurrencyLikeValue(valuation?.EnterpriseValue, currencyLabel),
        enterpriseValueEbitda: toNullableNumber(valuation?.EnterpriseValueEbitda),
        enterpriseValueEbitdaLabel: formatRatioValue(valuation?.EnterpriseValueEbitda),
        enterpriseValueRevenue: toNullableNumber(valuation?.EnterpriseValueRevenue),
        enterpriseValueRevenueLabel: formatRatioValue(valuation?.EnterpriseValueRevenue),
        metricSections,
        chartSections,
        institutionHolders,
        officers,
        activities,
        firstReportDate: formatDateLabel(toTimestamp(general?.UpdatedAt)),
        hasData,
        raw: payload
    };
}

export function normalizeChartingFundamentalEmployees(payload = []) {
    const employeeRows = toArray(payload)
        .map((item, index) => {
            const employees = toNullableNumber(item?.employees);
            const year = toStringValue(item?.year);

            if (!year || employees === null) {
                return null;
            }

            return {
                id: `${year}-${index}`,
                year,
                employees,
                employeesLabel: compactEmployees(employees),
                employeesFullLabel: formatFullNumber(employees, {
                    maximumFractionDigits: 0,
                    unit: 'empleados'
                }),
                sortValue: resolveSeriesSortValue(year, index)
            };
        })
        .filter(Boolean)
        .sort((left, right) => left.sortValue - right.sortValue);
    const hasNoDataSentinel = employeeRows.length === 1 && employeeRows[0].employees === 0;
    const visibleRows = hasNoDataSentinel ? [] : employeeRows;
    const labels = visibleRows.map((row) => row.year);
    const values = visibleRows.map((row) => row.employees);
    const latestRow = visibleRows[visibleRows.length - 1] || null;

    return {
        hasData: visibleRows.length > 0,
        hasNoDataSentinel,
        rows: visibleRows,
        chart: {
            id: 'employees',
            title: 'Numero de empleados',
            unitLabel: 'empleados',
            labels,
            values,
            latestLabel: latestRow?.employeesLabel || MISSING_VALUE_LABEL,
            hasData: visibleRows.length > 0,
            chartData: buildChartData({
                labels,
                values,
                title: 'Numero de empleados',
                colorIndex: 5
            })
        },
        raw: payload
    };
}
